import { useEffect, useState } from 'react';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import styles from "./TeamSchedule.module.css";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  selectedEndDate,
  selectedStartDate,
  setStartDate,
  setEndDate,
  fetchTeamScheduleAsync,
} from "../../app/slice/teamSchedule.slice";
import { TeamSelector } from '../teamSelector/TeamSelector';
import { ScheduleGrid } from '../scheduleGrid/ScheduleGrid';

const MAX_TEAMS = 7;

function parseDateParam(value: string | null): Date | null {
  if (!value) return null;
  const [year, month, day] = value.split('-').map(Number);
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day);
}

function formatDateParam(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function readUrlParams() {
  const params = new URLSearchParams(window.location.search);
  const teamsParam = params.get('teams');
  const teams = teamsParam
    ? teamsParam.split(',').map(Number).filter(n => n > 0)
    : null;
  return {
    teams: teams && teams.length > 0 ? teams : null,
    start: parseDateParam(params.get('start')),
    end: parseDateParam(params.get('end')),
  };
}

export function TeamSchedule() {
  const dispatch = useAppDispatch();
  const startDate = useAppSelector(state => selectedStartDate(state));
  const endDate = useAppSelector(state => selectedEndDate(state));

  const urlParams = readUrlParams();
  const [selectedTeamIds, setSelectedTeamIds] = useState<number[]>(
    urlParams.teams ?? [144, 147]
  );
  const [userSetStart, setUserSetStart] = useState(urlParams.start !== null);
  const [userSetEnd, setUserSetEnd] = useState(urlParams.end !== null);

  // Initialize Redux dates from URL on first render
  useEffect(() => {
    if (urlParams.start) dispatch(setStartDate(urlParams.start));
    if (urlParams.end) dispatch(setEndDate(urlParams.end));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleStartDateChange = (date: Date) => {
    setUserSetStart(true);
    dispatch(setStartDate(date));
    if (!userSetEnd) {
      const autoEnd = new Date(date);
      autoEnd.setDate(autoEnd.getDate() + 7);
      dispatch(setEndDate(autoEnd));
    }
  };

  const handleEndDateChange = (date: Date) => {
    setUserSetEnd(true);
    dispatch(setEndDate(date));
    if (!userSetStart) {
      const autoStart = new Date(date);
      autoStart.setDate(autoStart.getDate() - 7);
      dispatch(setStartDate(autoStart));
    }
  };

  useEffect(() => {
    for (const teamId of selectedTeamIds) {
      dispatch(fetchTeamScheduleAsync({ teamId, startDate, endDate }));
    }
  }, [dispatch, selectedTeamIds, startDate, endDate]);

  useEffect(() => {
    const params = new URLSearchParams();
    params.set('teams', selectedTeamIds.join(','));
    params.set('start', formatDateParam(startDate));
    params.set('end', formatDateParam(endDate));
    window.history.replaceState(null, '', `?${params.toString()}`);
  }, [selectedTeamIds, startDate, endDate]);

  const handleAddTeam = () => {
    if (selectedTeamIds.length < MAX_TEAMS) {
      setSelectedTeamIds(prev => [...prev, 144]);
    }
  };

  const handleRemoveTeam = (index: number) => {
    setSelectedTeamIds(prev => prev.filter((_, i) => i !== index));
  };

  const handleTeamChange = (index: number, teamId: number) => {
    setSelectedTeamIds(prev => prev.map((id, i) => (i === index ? teamId : id)));
  };

  return (
    <div className={styles.container}>
      <div className={styles.controls}>
        <span className={styles.dateLabel}>From</span>
        <div className={styles.dateSelectorContainer}>
          <DatePicker
            showIcon
            selected={startDate}
            onChange={(date) => { if (date) { handleStartDateChange(date) } }} />
          <span className={styles.dateSep}>→</span>
          <DatePicker
            showIcon
            selected={endDate}
            onChange={(date) => { if (date) { handleEndDateChange(date) } }} />
        </div>
      </div>
      <div
        className={styles.teamHeaders}
        style={{ gridTemplateColumns: `80px repeat(${selectedTeamIds.length}, 1fr)` }}
      >
        <div className={styles.teamHeaderSpacer} />
        {selectedTeamIds.map((teamId, index) => (
          <div key={index} className={styles.teamHeader}>
            <TeamSelector
              value={teamId}
              onTeamSelected={(id) => handleTeamChange(index, id)}
            />
            {selectedTeamIds.length > 1 && (
              <button
                className={styles.removeButton}
                onClick={() => handleRemoveTeam(index)}
                aria-label="Remove team"
              >
                ✕ remove
              </button>
            )}
          </div>
        ))}
      </div>
      {selectedTeamIds.length < MAX_TEAMS && (
        <div className={styles.addButtonRow}>
          <button className={styles.addButton} onClick={handleAddTeam}>
            + Add Team
          </button>
        </div>
      )}
      <ScheduleGrid selectedTeamIds={selectedTeamIds} />
    </div>
  );
}
